/**
 * AudioUploadModal
 *
 * Drives POST /connectors/{id}/upload-audio for an audio_upload connector.
 * Optional metadata fields (occurred_at, agent_identifier, customer_identifier,
 * language) are forwarded as form fields alongside the audio file.
 *
 * Upload is cancelled if the modal unmounts mid-request or the user clicks Cancel.
 */

import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { X, Loader2, UploadCloud } from 'lucide-react';

import FileDropzone from '../shared/FileDropzone';
import { uploadAudio } from '../../services/connectorService';

const MAX_BYTES = 100 * 1024 * 1024;
const ACCEPT = 'audio/*';

const nowLocalIsoMinutes = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const toIsoWithOffset = (localDatetime) => {
  if (!localDatetime) return '';
  const d = new Date(localDatetime);
  if (isNaN(d.getTime())) return '';
  return d.toISOString();
};

const AudioUploadModal = ({ isOpen, connector, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [occurredAt, setOccurredAt] = useState(nowLocalIsoMinutes());
  const [agent, setAgent] = useState('');
  const [customer, setCustomer] = useState('');
  const [language, setLanguage] = useState('');
  const [progress, setProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setFile(null);
      setOccurredAt(nowLocalIsoMinutes());
      setAgent('');
      setCustomer('');
      setLanguage('');
      setProgress(0);
      setError(null);
    }
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, [isOpen]);

  if (!isOpen || !connector) return null;

  const canSubmit = !!file && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file, file.name);
    const isoOccurred = toIsoWithOffset(occurredAt);
    if (isoOccurred) formData.append('occurred_at', isoOccurred);
    if (agent.trim()) formData.append('agent_identifier', agent.trim());
    if (customer.trim()) formData.append('customer_identifier', customer.trim());
    if (language.trim()) formData.append('language', language.trim());

    const controller = new AbortController();
    abortRef.current = controller;

    setSubmitting(true);
    setError(null);
    setProgress(0);

    const result = await uploadAudio(connector.id, formData, {
      signal: controller.signal,
      onProgress: (pct) => setProgress(pct),
    });

    if (abortRef.current === controller) abortRef.current = null;
    setSubmitting(false);

    if (!result.success) {
      setError(result.error || 'Upload failed.');
      toast.error(result.error || 'Upload failed.');
      return;
    }

    toast.success('Audio uploaded — processing will begin shortly.');
    onSuccess?.(result.data);
    onClose?.();
  };

  const handleClose = () => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    onClose?.();
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xl">
        <form onSubmit={handleSubmit}>
          <div className="flex items-center justify-between p-5 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <UploadCloud size={18} className="text-primary-600" />
              <h2 className="text-base font-semibold text-gray-900">
                Upload Audio to {connector.display_name}
              </h2>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          <div className="p-5 space-y-4">
            <FileDropzone
              accept={ACCEPT}
              maxBytes={MAX_BYTES}
              disabled={submitting}
              file={file}
              onFile={setFile}
              error={error}
              helpText="WAV, MP3, M4A, OGG, FLAC or WebM. Max 100 MB."
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs font-medium text-gray-700">Call time (optional)</span>
                <input
                  type="datetime-local"
                  value={occurredAt}
                  onChange={(e) => setOccurredAt(e.target.value)}
                  disabled={submitting}
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-700">Language hint (optional)</span>
                <input
                  type="text"
                  placeholder="en, hi-IN, ..."
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  disabled={submitting}
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-700">Agent (optional)</span>
                <input
                  type="text"
                  placeholder="Agent identifier"
                  value={agent}
                  onChange={(e) => setAgent(e.target.value)}
                  disabled={submitting}
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-700">Customer (optional)</span>
                <input
                  type="text"
                  placeholder="Customer phone or name"
                  value={customer}
                  onChange={(e) => setCustomer(e.target.value)}
                  disabled={submitting}
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                />
              </label>
            </div>

            {submitting && (
              <div>
                <div className="h-1.5 bg-gray-200 rounded overflow-hidden">
                  <div
                    className="h-full bg-primary-500 transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">{progress}%</p>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="text-sm border border-gray-300 rounded px-3 py-1.5 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="inline-flex items-center gap-1.5 text-sm text-white bg-primary-600 rounded px-3 py-1.5 hover:bg-primary-700 disabled:opacity-50"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
              <span>{submitting ? 'Uploading…' : 'Upload'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AudioUploadModal;
